// Page scroll lock with a counter per element (all sites had their own copy: popups, modals, sheets, sidebars).
// The first lock saves the element's current value, the last unlock puts it back, so two open layers never
// unlock each other and a closed layer never writes "auto" or "" over another one's lock.
// No imports, so it is tested in plain node (the element can be any object with `style` / `classList`).

// element -> Map(lock key -> { count, restore })
const locks = new WeakMap();

const NOOP = () => {};

const resolve_target = (target) => {
	if (typeof document === "undefined" && (target == null || typeof target === "string")) return null;
	if (target == null) return document.body;
	if (typeof target === "string") return document.querySelector(target);
	if (typeof target === "function") return target() || null;
	if (typeof target === "object" && "current" in target) return target.current || null;
	return target;
};

const split_classes = (class_name) => String(class_name || "").split(/\s+/).filter(Boolean);

const to_css_name = (property) => property.replace(/[A-Z]/g, (char) => `-${char.toLowerCase()}`);

// options:
// - target: element, ref, function returning an element, or a selector ("#main-content"). Default document.body.
// - mode: "style" (default) sets an inline style; "class" adds class names.
// - property / value: the style mode's property and value. Default "overflow" / "hidden".
// - class_name: the class mode's class names (space separated). Default "overflow-hidden".
// Returns an unlock function (safe to call more than once). Without a document or target it does nothing.
export const lock_body_scroll = (options = {}) => {
	const { target, mode = "style", property = "overflow", value = "hidden", class_name = "overflow-hidden" } = options;

	const element = resolve_target(target);
	if (!element) return NOOP;

	const is_class = mode === "class";
	const names = is_class ? split_classes(class_name) : [];
	const css_name = to_css_name(property);
	if (is_class && !names.length) return NOOP;

	const key = is_class ? `class:${names.join(" ")}` : `style:${css_name}:${value}`;

	let per_element = locks.get(element);
	if (!per_element) {
		per_element = new Map();
		locks.set(element, per_element);
	}

	let entry = per_element.get(key);
	if (!entry) {
		if (is_class) {
			// a class that was already there before the first lock stays after the last unlock
			const added = names.filter((name) => !element.classList.contains(name));
			element.classList.add(...added);
			entry = { count: 0, restore: () => added.length && element.classList.remove(...added) };
		} else {
			const previous = element.style.getPropertyValue(css_name);
			const previous_priority = element.style.getPropertyPriority(css_name);
			element.style.setProperty(css_name, value);
			entry = {
				count: 0,
				restore: () => {
					if (previous) element.style.setProperty(css_name, previous, previous_priority);
					else element.style.removeProperty(css_name);
				},
			};
		}
		per_element.set(key, entry);
	}

	entry.count += 1;

	let released = false;
	return () => {
		if (released) return;
		released = true;
		entry.count -= 1;
		if (entry.count > 0) return;
		per_element.delete(key);
		entry.restore();
	};
};

// How many locks hold this element now (all keys). For tests and debugging.
export const scroll_lock_count = (target) => {
	const element = resolve_target(target);
	const per_element = element && locks.get(element);
	if (!per_element) return 0;
	let total = 0;
	per_element.forEach((entry) => {
		total += entry.count;
	});
	return total;
};
